import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigationModal from './NavigationModal';
import { DiscountApprovalModal } from './ui';
import { QuickAddExpense } from './Expenses';
import { ShoppingCart, LogOut, Store, Menu, BarChart3, Package, RefreshCw } from 'lucide-react';
import { hasAnalyticsAccess } from '../utils/accessControl';
import { config } from '../config/env';
import { useAppStore } from '../stores/useAppStore';

const Layout = ({ children }) => {
  const appIcon = config.appIcon;
  const { user, logout, isAuthenticated, isSalesman } = useAuth();
  const { itemCount } = useCart();
  const { isNavigationModalOpen, openNavigationModal, closeNavigationModal } = useNavigation();
  const { businessInfo } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);

  // Global Ctrl+E listener for quick expense add
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        setIsExpenseModalOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Redirect salesmen to scanner page if they try to access other pages
  useEffect(() => {
    if (isAuthenticated && isSalesman && location.pathname !== '/scanner') {
      navigate('/scanner');
    }
  }, [isAuthenticated, isSalesman, location.pathname, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };


  if (!isAuthenticated) {
    return <>{children}</>;
  }


  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Navigation button, Logo and Mobile menu */}
            <div className="flex items-center gap-3">
              {/* Navigation Button - Hidden on mobile */}
              <button
                onClick={openNavigationModal}
                className="hidden md:flex w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full items-center justify-center transition-all duration-200 text-gray-600 hover:text-gray-900 relative"
                title="Open Navigation (Ctrl+M)"
              >
                <Menu className="h-5 w-5" />
                {/* Current page indicator */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full"></div>
              </button>

              {/* Mobile menu button - Opens navigation modal directly */}
              <button
                onClick={openNavigationModal}
                className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              <div className="flex items-center gap-3">
                <img src={appIcon} alt="App Icon" className="h-9 w-9 rounded-lg object-contain" />
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {businessInfo?.BusinessName
                    ? [businessInfo.BusinessName, businessInfo.BranchLocation?.trim()].filter(Boolean).join(' - ')
                    : config.appTitle}
                </h1>
              </div>
            </div>


            {/* Right side - User info */}
            <div className="flex items-center gap-3">
              {/* User Info */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-700 hidden sm:block">
                    {user?.full_name || user?.username}
                  </span>
                </div>
                
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:block text-sm font-medium">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>


      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>

      {/* Navigation Modal */}
      <NavigationModal 
        isOpen={isNavigationModalOpen} 
        onClose={closeNavigationModal}
      />

      {/* Discount Approval Modal */}
      <DiscountApprovalModal />

      {/* Quick Add Expense Modal */}
      <QuickAddExpense
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
      />
    </div>
  );
};

export default Layout;
